import toast, { Toaster } from "react-hot-toast";
import BackButtonWrapper from "../../components/button/BackButton/wrapper";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import useFormWithSchema from "../../hooks/useFormWithSchema";
import { Button } from "../../components/button";
import {
  StartEvacuationSchema,
  StartEvacuationFormData,
} from "../../schemas/startEvacuationSchema";
import { useEvacuationSelections } from "../../hooks/emergency-evacuation/useEvacuationSelections";
import { useEvacuationData } from "../../hooks/emergency-evacuation/useEvacuationData";
import ScopeSection from "./components/ScopeSection";
import ScopeDisplay from "./components/ScopeDisplay";
import MusterPointsSection from "./components/MusterPointsSection";
import EventTypeSection from "./components/EventTypeSection";
import {
  useIsEvacuationExists,
  useStartEvacuation,
  useUpdateEvacuation,
} from "../../hooks/emergency-evacuation/mutation";
import { useLocation, useNavigate } from "react-router-dom";
import { APP_ROUTES } from "../../routes";
import { useGetEvacuationEventById } from "../../hooks/emergency-evacuation/query";
import {
  EVACUATION_EVENT_STATUSES,
  EVACUATION_START_UPDATE_TYPE,
} from "../../constants/common";

const StartEvacuation = () => {
  const { t, i18n } = useTranslation();

  const navigate = useNavigate();
  const location = useLocation();

  const {
    selectedMusterPoints,
    selectedEventType,
    selectedWhoToInclude,
    toggleMusterPoint,
    toggleEventType,
    // toggleWhoToInclude,
    initializeWhoToInclude,
    clearMusterPoints,
    setSelectedEventType,
    setSelectedMusterPoints,
  } = useEvacuationSelections();

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    watch,
  } = useFormWithSchema(StartEvacuationSchema);

  const selectedLocationId = watch("location");
  const selectedBuildingId = watch("building");
  const floor = watch("floor");

  const searchParams = new URLSearchParams(location.search);
  const key = searchParams.get("ID");

  const { data: evacuationEvent } = useGetEvacuationEventById({
    encryptedId: key || "",
  });

  const {
    locations,
    buildings,
    musterPointsOptions,
    eventTypes,
    // whoToIncludes,
    eventStatus,
  } = useEvacuationData(
    selectedLocationId,
    selectedBuildingId,
    initializeWhoToInclude,
    evacuationEvent,
  );

  const [evacuationStatus, setEvacuationStatus] =
    useState<EVACUATION_EVENT_STATUSES | null>(null);

  useEffect(() => {
    if (!key) {
      setValue("location", "");
      setValue("building", "");
      setValue("floor", "");

      clearMusterPoints();
      setSelectedEventType(null);
      initializeWhoToInclude([]);

      setEvacuationStatus(null);

      return;
    }

    if (!evacuationEvent) return;

    setValue("location", evacuationEvent.n_LocationID.toString());
    setValue("building", evacuationEvent.n_BuildingID.toString());
    setValue("floor", evacuationEvent.s_FloorNo);

    clearMusterPoints();
    setSelectedEventType(null);
    initializeWhoToInclude([]);

    if (
      evacuationEvent.musterPoints &&
      evacuationEvent.musterPoints.length > 0
    ) {
      const musterPointIds = evacuationEvent.musterPoints?.map(
        (musterPoint: { n_MusterPointID: number }) =>
          musterPoint.n_MusterPointID,
      );
      setSelectedMusterPoints(musterPointIds);
    }

    if (evacuationEvent.s_EventTypeCode) {
      setSelectedEventType(Number(evacuationEvent.s_EventTypeCode));
    }

    if (evacuationEvent.s_EventPersonTypeCode) {
      const personTypeCodes = evacuationEvent.s_EventPersonTypeCode
        .split(",")
        .map((code: string) => Number(code));
      initializeWhoToInclude(personTypeCodes);
    }

    setEvacuationStatus(evacuationEvent.s_StatusCode);
  }, [
    key,
    clearMusterPoints,
    evacuationEvent,
    initializeWhoToInclude,
    setValue,
    setSelectedEventType,
    setSelectedMusterPoints,
  ]);

  const isEventClosed = useMemo(
    () => evacuationStatus === EVACUATION_EVENT_STATUSES.CLOSED,
    [evacuationStatus],
  );

  const {
    mutateAsync: IsEvacuationExists,
    isPending: isEvacuationExistsLoading,
  } = useIsEvacuationExists();
  const { mutateAsync: StartEvacuation } = useStartEvacuation();
  const { mutateAsync: UpdateEvacuation } = useUpdateEvacuation();

  const handleFormSubmit = useCallback(
    async (
      data: StartEvacuationFormData,
      type: EVACUATION_START_UPDATE_TYPE,
    ) => {
      if (musterPointsOptions.length === 0) {
        toast.error(t("cannotStartEvacuationNoMusterPoints"));
        return;
      } else if (selectedMusterPoints.length === 0) {
        toast.error(t("pleaseSelectAtLeastOneMusterPoint"));
        return;
      } else if (!selectedEventType) {
        toast.error(t("pleaseSelectEvacuationEventType"));
        return;
      }
      //  else if (selectedWhoToInclude.length === 0) {
      //   toast.error(t("pleaseSelectAtLeastOneWhoToIncludeOption"));
      //   return;
      // }

      try {
        const isExistBody = {
          n_EvacuationID: Number(key) || 0,
          n_LocationID: Number(data.location),
          n_BuildingID: Number(data.building),
          s_MusterPointIDs: selectedMusterPoints.join(","),
          s_EventTypeCode: selectedEventType.toString(),
        };

        const { isExists } = await IsEvacuationExists(isExistBody);

        if (isExists) {
          toast.error(t("evacuationAlreadyExists"));
          return;
        }
      } catch (error) {
        toast.error(t("somethingWentWrong"));
        return;
      }

      try {
        const body = {
          n_LocationID: Number(data.location),
          n_BuildingID: Number(data.building),
          s_FloorNo: data.floor || "",
          s_EventTypeCode: selectedEventType.toString(),
          s_EventPersonTypeCode: selectedWhoToInclude.join(","),
          dt_EventStartTime: new Date().toISOString(),
          musterPoints: selectedMusterPoints.map((pointId) => ({
            n_MusterPointID: pointId,
          })),
          IsSentAlerts:
            type === EVACUATION_START_UPDATE_TYPE.START_AND_SEND_ALERT ||
            type === EVACUATION_START_UPDATE_TYPE.UPDATE_AND_SEND_ALERT,
        };

        if (!key) {
          await StartEvacuation(body);
          toast.success(t("evacuationStartedSuccessfully"));

          setTimeout(() => {
            navigate(APP_ROUTES.LIVE_EVACUATION_DASHBOARD);
          }, 700);
        } else {
          await UpdateEvacuation({ ...body, n_EvacuationID: Number(key) });
          toast.success(t("evacuationUpdatedSuccessfully"));

          setTimeout(() => {
            navigate(APP_ROUTES.EVACUATION_EVENTS);
          }, 700);
        }
      } catch (error) {
        toast.error(t("somethingWentWrong"));
      }
    },
    [
      IsEvacuationExists,
      StartEvacuation,
      UpdateEvacuation,
      key,
      musterPointsOptions.length,
      navigate,
      selectedEventType,
      selectedMusterPoints,
      selectedWhoToInclude,
      t,
    ],
  );

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <div className="flex flex-col gap-4 md:gap-8">
        <BackButtonWrapper>
          <h1 className="text-xl md:text-2xl dark:text-white">
            {!key ? t("startEvacuation") : t("updateEvacuation")}
          </h1>
        </BackButtonWrapper>

        {isEventClosed && (
          <div className="bg-gray-500 px-4 py-1 rounded-md flex items-center gap-2 w-fit -my-3">
            <p className="text-white text-sm">
              {i18n.language === "en"
                ? eventStatus?.statusEn
                : eventStatus?.statusAr}
            </p>
          </div>
        )}

        <form className="flex flex-col gap-4 md:gap-8">
          <div className="flex flex-col gap-3 lg:gap-6 bg-white p-4 rounded-xl shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
            <ScopeSection
              control={control}
              errors={errors}
              locations={locations}
              buildings={buildings}
              setValue={setValue}
              clearMusterPoints={clearMusterPoints}
              disabled={isEventClosed}
            />

            <ScopeDisplay
              selectedLocationId={selectedLocationId}
              selectedBuildingId={selectedBuildingId}
              floor={floor}
              locations={locations}
              buildings={buildings}
              disabled={isEventClosed}
            />

            <MusterPointsSection
              selectedLocationId={selectedLocationId}
              musterPointsOptions={musterPointsOptions}
              selectedMusterPoints={selectedMusterPoints}
              onToggleMusterPoint={toggleMusterPoint}
              disabled={isEventClosed}
            />

            <EventTypeSection
              eventTypes={eventTypes}
              selectedEventType={selectedEventType}
              onToggleEventType={toggleEventType}
              sectionNumber={selectedLocationId ? 3 : 2}
              disabled={isEventClosed}
            />

            {/* <WhoToIncludeSection
              whoToIncludes={whoToIncludes}
              selectedWhoToInclude={selectedWhoToInclude}
              onToggleWhoToInclude={toggleWhoToInclude}
              sectionNumber={selectedLocationId ? 4 : 3}
              disabled={isEventClosed}
            /> */}

            {!isEventClosed && (
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  className="bg-red-600 hover:bg-red-700"
                  onClick={handleSubmit((data) =>
                    handleFormSubmit(
                      data,
                      !key
                        ? EVACUATION_START_UPDATE_TYPE.START
                        : EVACUATION_START_UPDATE_TYPE.UPDATE,
                    ),
                  )}
                  isLoading={isEvacuationExistsLoading}
                >
                  {!key ? t("startEvacuation") : t("updateEvacuation")}
                </Button>
                <Button
                  onClick={handleSubmit((data) =>
                    handleFormSubmit(
                      data,
                      !key
                        ? EVACUATION_START_UPDATE_TYPE.START_AND_SEND_ALERT
                        : EVACUATION_START_UPDATE_TYPE.UPDATE_AND_SEND_ALERT,
                    ),
                  )}
                  isLoading={isEvacuationExistsLoading}
                >
                  {!key ? t("startAndSendAlert") : t("updateAndSendAlert")}
                </Button>
              </div>
            )}
          </div>
        </form>
      </div>
    </>
  );
};

export default StartEvacuation;
